import React, { useCallback, useEffect, useMemo, useState } from "react";
import { tableUseStyle } from "../../etc/tableusestyle";
import { DataGridPremium, GridColDef, GridToolbar } from "@mui/x-data-grid-premium";
import { Button, Select, Option } from "@mui/joy";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import CustomInput from "../../etc/custominput";
import WinterPresentationManualAddModal from "./winterpresentationmanualaddmodal";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

/**
 * 본사직원페이지 > 윈터 스쿨 등록 관리 > '윈터스쿨 설명회 신청자 관리' — suneungsunbae.com/2027winterpresentation 설명회 예약자 리스트.
 * winterregister.tsx(사전예약 리스트)와 같은 구성. 서버: /dashboard/winterPresentation/list · /memo · /hide · /manual · /seatStatus (users.todoistApiKey 보유자 전용).
 * - 상단 '회차별 접수 상태' 카드: 여유/임박/마감 을 바꾸면 즉시 저장되고 랜딩 회차 버튼 배지(접수 가능/마감 임박/대기 등록)에 반영된다(랜딩 새로고침 기준).
 * - '수동 추가하기'로 넣은 행(isManual = 1)은 목록·집계표에는 들어가지만 일별 추이 그래프에서는 뺀다.
 * - 목록은 서버가 숨기지 않은(isHide = 0) 행만 내려준다. 집계표도 그 행 기준.
 * - 메모 셀은 더블클릭 편집 → processRowUpdate 에서 서버 저장, 실패 시 이전 값으로 롤백.
 * - 행 클릭 후 '숨기기' → 서버 isHide = 1, 화면에서는 되돌릴 수 없다(해제는 MySQL 에서 직접).
 */

// 회차 — 서버 router/winterPresentation.ts SESSIONS · 랜딩 data/winterpresentation.ts PRES_SESSIONS 와 같은 순서
const PRES_SESSIONS = [
    { round: 1, location: "센텀", label: "11/21(토) 센텀", short: "11/21 센텀" },
    { round: 2, location: "수성", label: "11/22(일) 수성", short: "11/22 수성" },
    { round: 3, location: "김포", label: "11/29(일) 김포", short: "11/29 김포" },
    { round: 4, location: "강동", label: "12/5(토) 강동", short: "12/5 강동" },
];
const PRES_GRADES = ["예비 고1", "예비 고2", "예비 고3", "N수"];
type SeatStatus = "여유" | "임박" | "마감";
const SEAT_STATUSES: SeatStatus[] = ["여유", "임박", "마감"];
// 랜딩 회차 버튼 배지 문구·색 (suneungclient3 data/winterpresentation.ts presBadge 와 동일)
const SEAT_BADGE: Record<SeatStatus, { t: string, c: string, bg: string }> = {
    "여유": { t: "접수 가능", c: "#087A4B", bg: "#E6F4EC" },
    "임박": { t: "마감 임박", c: "#A95B00", bg: "#FFF1D6" },
    "마감": { t: "대기 등록", c: "#1F5D96", bg: "#E9F2FC" },
};

const roundLabel = (round: any) => {
    const s = PRES_SESSIONS.find((x) => x.round === +round);
    return s ? s.label : String(round || "");
};

// 차트 테마 — charts/InflowPage.tsx 와 동일
const CARD_BG = "#FFFFFF";
const BORDER = "#E5E7EB";
const TEXT_MAIN = "#111827";
const TEXT_SUB = "#6B7280";
const LINE_COLOR = "#185FA5";

const toDateKey = (v: any) => {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const toastOptions: any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored",
};

const formatDate = (v: any) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const formatPhone = (v: any) => {
    const d = String(v || "").replace(/[^0-9]/g, "");
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    return String(v || "");
};

const WinterPresentationList: React.FC<any> = (props) => {

    const classes = tableUseStyle();

    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [round, setRound] = useState<number>(0);          // 0 = 전체
    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const [hiding, setHiding] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>("");
    const [chartRound, setChartRound] = useState<number>(0);
    const [manualOpen, setManualOpen] = useState<boolean>(false);
    const [seat, setSeat] = useState<Record<number, SeatStatus>>({ 1: "여유", 2: "여유", 3: "여유", 4: "여유" });
    const [seatSaving, setSeatSaving] = useState<number>(0); // 저장 중인 회차 (0 = 없음)

    const fetchList = useCallback(async () => {
        try {
            setLoading(true);
            setSelectedRow(null);
            const result: any = await getFetch("/dashboard/winterPresentation/list");
            if (result.message === "success" && Array.isArray(result.data)) {
                setRows(result.data);
            } else {
                console.log(result);
                toast.error("목록을 불러오지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("목록을 불러오지 못했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    }, []);

    // 회차별 접수 상태 — 목록과 별개 요청. 실패해도 기본값(여유)으로 화면은 뜬다
    const fetchSeat = useCallback(async () => {
        try {
            const result: any = await getFetch("/dashboard/winterPresentation/seatStatus");
            if (result.message === "success" && Array.isArray(result.data)) {
                const next: Record<number, SeatStatus> = { 1: "여유", 2: "여유", 3: "여유", 4: "여유" };
                result.data.forEach((row: any) => {
                    if (next[+row.round] !== undefined && SEAT_STATUSES.includes(row.status)) next[+row.round] = row.status;
                });
                setSeat(next);
            } else {
                console.log(result);
            }
        } catch (e) {
            console.log(e);
        }
    }, []);

    useEffect(() => {
        fetchList();
        fetchSeat();
    }, [fetchList, fetchSeat]);

    // 접수 상태 변경 — 즉시 저장, 실패하면 이전 값으로 롤백
    const changeSeat = async (r: number, status: SeatStatus) => {
        const prev = seat[r];
        if (prev === status || seatSaving) return;
        const label = PRES_SESSIONS.find((s) => s.round === r)?.location || `${r}회차`;
        setSeat((p) => ({ ...p, [r]: status }));
        try {
            setSeatSaving(r);
            const result: any = await postFetch("/dashboard/winterPresentation/seatStatus", { round: r, status });
            if (result.message !== "success") throw new Error(result.message);
            toast.success(`${label} 회차를 '${SEAT_BADGE[status].t}'으로 바꿨습니다`, toastOptions);
        } catch (e) {
            console.log(e);
            setSeat((p) => ({ ...p, [r]: prev }));
            toast.error("접수 상태 저장에 실패했습니다. 다시 시도해 주세요.", toastOptions);
        } finally {
            setSeatSaving(0);
        }
    };

    // 필터 토글 + 검색어에 따른 표시 행 — 집계표는 필터와 무관하게 rows 전체 기준
    // 검색은 allstudents.tsx nameChange 와 같은 규칙: 첫 글자가 숫자면 전화번호, 아니면 이름 contains
    const visibleRows = useMemo(() => {
        const text = searchText.trim();
        const isNumber = text.length > 0 && !isNaN(+text[0]);
        return rows.filter((row: any) => {
            if (round !== 0 && +row.round !== round) return false;
            if (!text) return true;
            if (isNumber) return String(row.phoneNumber || "").replace(/[^0-9]/g, "").includes(text.replace(/[^0-9]/g, ""));
            return String(row.name || "").includes(text);
        });
    }, [rows, round, searchText]);

    // 회차별 · 학년별 신청자 수 (서버가 이미 숨긴 행을 뺀 상태)
    const roundCounts = useMemo(() => {
        const counts: Record<number, number> = {};
        PRES_SESSIONS.forEach((s) => { counts[s.round] = 0; });
        rows.forEach((row: any) => {
            const r = +row.round;
            if (counts[r] === undefined) counts[r] = 0;
            counts[r] += 1;
        });
        return counts;
    }, [rows]);

    const gradeCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        PRES_GRADES.forEach((g) => { counts[g] = 0; });
        rows.forEach((row: any) => {
            const g = String(row.grade || "기타");
            if (counts[g] === undefined) counts[g] = 0;
            counts[g] += 1;
        });
        return counts;
    }, [rows]);

    // 목록에 없는 학년 값이 DB에 있으면 집계표 맨 아래에 같이 보여준다
    const summaryGrades = useMemo(() => {
        const extra = Object.keys(gradeCounts).filter((g) => !PRES_GRADES.includes(g));
        return [...PRES_GRADES, ...extra];
    }, [gradeCounts]);

    // 일별 신청자 수 — 첫 신청일부터 오늘까지 빈 날은 0으로 채운다. 회차 칩은 표 필터와 별개. 수동 추가(isManual)는 제외
    const chartData = useMemo(() => {
        const source = rows.filter((row: any) => !row.isManual);
        const target = chartRound === 0 ? source : source.filter((row: any) => +row.round === chartRound);
        if (source.length === 0) return [];
        const counts: Record<string, number> = {};
        target.forEach((row: any) => {
            const key = toDateKey(row.createdAt);
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
        });
        // 기간은 회차와 무관하게 전체 첫 신청일 ~ 오늘 (회차 바꿔도 x축 고정)
        let minTime = Infinity;
        source.forEach((row: any) => {
            const t = new Date(row.createdAt).getTime();
            if (!isNaN(t) && t < minTime) minTime = t;
        });
        if (minTime === Infinity) return [];
        const cursor = new Date(minTime);
        cursor.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const result: { date: string, count: number }[] = [];
        while (cursor.getTime() <= today.getTime()) {
            const key = toDateKey(cursor);
            result.push({ date: key, count: counts[key] || 0 });
            cursor.setDate(cursor.getDate() + 1);
        }
        return result;
    }, [rows, chartRound]);

    const columns: GridColDef[] = [
        {
            field: "round",
            headerName: "회차",
            width: 130,
            valueGetter: (params: any) => roundLabel(params.value),
        },
        { field: "grade", headerName: "학년", width: 90 },
        { field: "name", headerName: "이름", width: 110 },
        {
            field: "phoneNumber",
            headerName: "전화번호",
            width: 140,
            valueFormatter: (params: any) => formatPhone(params.value),
        },
        {
            field: "createdAt",
            headerName: "신청일시",
            width: 150,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        { field: "memo", headerName: "메모", width: 260, flex: 1, editable: true },
    ];

    // 메모 편집 저장 — 실패하면 이전 행으로 되돌린다
    const handleRowUpdate = async (newRow: any, oldRow: any) => {
        try {
            if ((newRow.memo || "") !== (oldRow.memo || "")) {
                const r: any = await postFetch("/dashboard/winterPresentation/memo", { id: newRow.id, memo: newRow.memo || "" });
                if (r.message !== "success") throw new Error(r.message);
            }
            return newRow;
        } catch (e) {
            console.log(e);
            toast.error("메모 저장에 실패했습니다. 다시 시도해 주세요.", toastOptions);
            return oldRow;
        }
    };

    // 선택한 행 숨기기 — 성공 시 목록에서 제거(재조회 없음)
    const handleHide = async () => {
        if (!selectedRow || hiding) return;
        const target = selectedRow;
        try {
            setHiding(true);
            const r: any = await postFetch("/dashboard/winterPresentation/hide", { id: target.id });
            if (r.message !== "success") throw new Error(r.message);
            setRows((prev) => prev.filter((row: any) => row.id !== target.id));
            setSelectedRow(null);
            toast.success(`${target.name} 신청을 숨김 처리했습니다`, toastOptions);
        } catch (e) {
            console.log(e);
            toast.error("숨기기에 실패했습니다. 다시 시도해 주세요.", toastOptions);
        } finally {
            setHiding(false);
        }
    };

    const cellStyle: React.CSSProperties = {
        padding: "8px 16px",
        borderBottom: "1px solid #e5e8eb",
        fontFamily: "Apple_R",
        fontSize: "14px",
        color: "#191f28",
    };

    const summaryTable = (title: string, items: { label: string, count: number }[]) => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
            <div style={{ fontFamily: "Apple_B", fontSize: "16px", marginBottom: "10px", width: "280px" }}>
                {title}
            </div>
            <table style={{ borderCollapse: "collapse", width: "280px", border: "1px solid #e5e8eb" }}>
                <tbody>
                    <tr style={{ backgroundColor: "#f5f6f8" }}>
                        <td style={{ ...cellStyle, fontFamily: "Apple_B" }}>총 신청자 수</td>
                        <td style={{ ...cellStyle, fontFamily: "Apple_B", textAlign: "right" }}>{rows.length}명</td>
                    </tr>
                    {items.map((item, index) => {
                        const isLast = index === items.length - 1;
                        const rowCellStyle = isLast ? { ...cellStyle, borderBottom: "none" } : cellStyle;
                        return (
                            <tr key={item.label}>
                                <td style={rowCellStyle}>{item.label}</td>
                                <td style={{ ...rowCellStyle, textAlign: "right" }}>{item.count}명</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div style={{ width: "96%" }}>
            <div style={{
                fontFamily: "Apple_B",
                marginTop: "50px",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
            }}>
                <div>
                    윈터스쿨 설명회 신청자 관리
                </div>
            </div>

            {/* 회차별 접수 상태 — 랜딩 회차 버튼 배지에 반영 */}
            <div style={{ marginTop: "20px", background: CARD_BG, borderRadius: 12, padding: "18px 24px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4, fontFamily: "Apple_B" }}>회차별 접수 상태</div>
                <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 14 }}>랜딩 페이지(/2027winterpresentation) 회차 버튼의 배지에 바로 반영됩니다(새로고침 기준). 여유 → 접수 가능 · 임박 → 마감 임박 · 마감 → 대기 등록</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 28px" }}>
                    {PRES_SESSIONS.map((s) => {
                        const st = seat[s.round] || "여유";
                        const badge = SEAT_BADGE[st];
                        return (
                            <div key={s.round} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ fontSize: 13, color: TEXT_MAIN, fontFamily: "Apple_R", minWidth: "96px" }}>{s.label}</span>
                                <Select
                                    size="sm"
                                    value={st}
                                    disabled={seatSaving !== 0}
                                    onChange={(e, value) => { if (value) changeSeat(s.round, value as SeatStatus); }}
                                    sx={{ minWidth: "92px" }}
                                >
                                    {SEAT_STATUSES.map((v) => (
                                        <Option key={v} value={v}>{v}</Option>
                                    ))}
                                </Select>
                                <span style={{ display: "inline-flex", alignItems: "center", padding: "5px 8px", background: badge.bg, color: badge.c, fontSize: 12, fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap" }}>{badge.t}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", marginTop: "24px", flexWrap: "wrap", gap: "12px" }}>
                <ToggleButtonGroup
                    color="primary"
                    size="small"
                    exclusive
                    value={round}
                    onChange={(e, value) => {
                        if (value === null || value === undefined) return;
                        setRound(+value);
                        setSelectedRow(null);
                    }}
                >
                    <ToggleButton value={0}>전체</ToggleButton>
                    {PRES_SESSIONS.map((s) => (
                        <ToggleButton value={s.round} key={s.round}>{s.short}</ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "16px" }}>
                    <CustomInput
                        value={searchText}
                        onChange={(e: any) => { setSearchText(e.target.value); setSelectedRow(null); }}
                        placeholder="이름 or 전화번호"
                    />
                    <Button size="sm" variant="solid" onClick={() => setManualOpen(true)}>
                        수동 추가하기
                    </Button>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        disabled={!selectedRow}
                        loading={hiding}
                        onClick={handleHide}
                    >
                        {selectedRow ? `숨기기 (${selectedRow.name})` : "숨기기"}
                    </Button>
                </div>
            </div>

            <div className={classes.root} style={{ height: "560px", width: "100%", marginTop: "16px", backgroundColor: "white" }}>
                <DataGridPremium
                    rows={visibleRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedRow ? [selectedRow.id] : []}
                    onRowClick={(params) => setSelectedRow(params.row)}
                    processRowUpdate={handleRowUpdate}
                    onProcessRowUpdateError={(e) => console.log(e)}
                    slots={{ toolbar: GridToolbar }}
                />
            </div>

            <div style={{ marginTop: "28px", marginBottom: "60px", display: "flex", alignItems: "flex-start", gap: "24px" }}>
                {/* 일별 신청자 추이 — winterregister.tsx 와 같은 구성, 지점 칩 대신 회차 칩 */}
                <div style={{ flex: 1, minWidth: 0, background: CARD_BG, borderRadius: 12, padding: "20px 24px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4, fontFamily: "Apple_B" }}>일별 신청자 추이</div>
                    <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 12 }}>회차 칩을 클릭하면 해당 회차만 표시</div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingBottom: 14, borderBottom: `1px dashed ${BORDER}`, marginBottom: 12 }}>
                        <span style={{ fontSize: 12, color: TEXT_SUB, marginRight: 4 }}>회차</span>
                        {[{ round: 0, short: "전체" }, ...PRES_SESSIONS].map((s) => {
                            const active = chartRound === s.round;
                            return (
                                <button
                                    key={s.round}
                                    onClick={() => setChartRound(s.round)}
                                    style={{
                                        padding: "4px 14px", borderRadius: 20,
                                        border: `1px solid ${active ? "#4F46E5" : BORDER}`,
                                        background: active ? "#4F46E5" : CARD_BG,
                                        color: active ? "#fff" : TEXT_SUB,
                                        cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400,
                                    }}
                                >
                                    {s.short}
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 10 }}>
                        현재: {chartRound === 0 ? "전체 합계" : roundLabel(chartRound)} · 첫 신청일부터 오늘까지 · 수동 추가분 제외
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart key={chartRound} data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                            <XAxis
                                dataKey="date"
                                tick={{ fill: TEXT_SUB, fontSize: 10 }}
                                tickFormatter={(v: string) => {
                                    if (!v) return "";
                                    const parts = v.split("-");
                                    if (parts.length === 3) return `${+parts[1]}.${+parts[2]}`;
                                    return v;
                                }}
                            />
                            <YAxis allowDecimals={false} tick={{ fill: TEXT_SUB, fontSize: 11 }} />
                            <Tooltip
                                contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }}
                                formatter={(value: any) => [`${value}명`, "신청자"]}
                            />
                            <Line type="monotone" dataKey="count" stroke={LINE_COLOR} strokeWidth={2} dot={false} name="신청자" isAnimationActive={true} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                    {summaryTable("회차별 집계", PRES_SESSIONS.map((s) => ({ label: s.label, count: roundCounts[s.round] || 0 })))}
                    {summaryTable("학년별 집계", summaryGrades.map((g) => ({ label: g, count: gradeCounts[g] || 0 })))}
                </div>
            </div>

            <WinterPresentationManualAddModal
                open={manualOpen}
                onClose={() => setManualOpen(false)}
                sessions={PRES_SESSIONS}
                grades={PRES_GRADES}
                onSuccess={(row: any) => {
                    // 재조회 없이 맨 위에 추가 (목록은 createdAt DESC)
                    setRows((prev) => [row, ...prev]);
                    setSelectedRow(null);
                }}
            />
        </div>
    );
};

export default WinterPresentationList;
